'use client';

/**
 * Wild TV hero — full-bleed WebTV-style opener: embedded top nav,
 * massive stacked title, circular play control, accent "next" button,
 * and a scrollable episode-thumbnail rail.
 *
 * <p>Companion to the {@code WildTvHeroView} admin canvas preview in
 * {@code admin/src/components/app/blockViews.tsx} — that renders a
 * static mock for the editor; this is the real, responsive block
 * (bleeds edge-to-edge via the page-bleed CSS vars, same mechanism
 * {@code HeroCinematic} uses).
 */
import { useTranslation } from 'react-i18next';
import { Play, ChevronRight } from 'lucide-react';
import { tx, asList } from '../_shared/coerce';
import { PortalLink } from '@akademi/site-builder-runtime';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface WildTvHeroNavItem {
  label?: Localized;
  href?: string;
}

export interface WildTvHeroEpisode {
  label?: Localized;
  title?: Localized;
  imageUrl?: string;
  duration?: string;
  href?: string;
}

export interface WildTvHeroProps {
  brandText?: string;
  brandBadge?: string;
  navItems?: WildTvHeroNavItem[];
  joinLabel?: Localized;
  joinHref?: string;
  eyebrow?: Localized;
  title?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  heroImageUrl?: string;
  heroImagePosition?: string;
  playHref?: string;
  nextHref?: string;
  episodesLabel?: Localized;
  episodes?: WildTvHeroEpisode[];
  /** {@code medium} → 60vh, {@code large} → 80vh, {@code screen} (default) → 100vh. */
  height?: 'screen' | 'large' | 'medium';
  accentColor?: string;
}

export function WildTvHero({
  brandText,
  brandBadge = 'TV',
  navItems,
  joinLabel,
  joinHref = '/plans',
  eyebrow,
  title,
  ctaLabel,
  ctaHref = '/episodes',
  heroImageUrl,
  heroImagePosition = 'right center',
  playHref = '#',
  nextHref,
  episodesLabel,
  episodes,
  height = 'screen',
  accentColor = '#ff4b45',
}: WildTvHeroProps) {
  const { t } = useTranslation('site-builder-blocks');
  const brandName = brandText ?? t('hero.wildTv.brand');
  const joinText = joinLabel ?? t('hero.wildTv.join');
  const nav = asList<WildTvHeroNavItem>(navItems);
  const eps = asList<WildTvHeroEpisode>(episodes);
  const titleLines = (tx(title) || 'WILD\nEXTREME').split(/\n|\|/g).filter(Boolean);
  const rail = tx(ctaLabel) || tx(episodesLabel);

  const heightStyle =
    height === 'large'
      ? 'min(80vh, 720px)'
      : height === 'medium'
        ? 'min(60vh, 560px)'
        : 'min(100vh, 900px)';

  return (
    <BlockRoot
      as="section"
      style={{
        position: 'relative',
        marginTop: 0,
        marginLeft: 'var(--page-bleed-margin, calc(50% - 50vw))',
        marginRight: 'var(--page-bleed-margin, calc(50% - 50vw))',
        width: 'var(--page-bleed-width, 100vw)',
        maxWidth: 'var(--page-bleed-width, 100vw)',
        height: heightStyle,
        overflow: 'hidden',
        background: '#050505',
        color: '#fff',
        fontFamily: 'var(--font-family-display, "Arial Black", system-ui, sans-serif)',
      }}
    >
      {heroImageUrl && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("${heroImageUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: heroImagePosition,
          }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at 78% 34%, color-mix(in srgb, ${accentColor} 30%, transparent), transparent 45%), linear-gradient(90deg, #050505 0%, rgba(5,5,5,0.7) 52%, rgba(5,5,5,0.35) 100%)`,
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
          padding: 'clamp(16px, 3vw, 32px) clamp(16px, 4vw, 48px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 900,
              fontSize: 'clamp(20px, 2.4vw, 28px)',
              letterSpacing: '-0.04em',
            }}
          >
            <span>{brandName}</span>
            <span
              style={{
                border: '2px solid #fff',
                borderRadius: 3,
                padding: '1px 5px',
                fontSize: '0.6em',
              }}
            >
              {brandBadge}
            </span>
          </div>
          {(nav.length > 0 || joinText) && (
            <div
              className="wild-tv-hero-nav"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(12px, 2vw, 20px)',
                fontFamily: 'Arial, sans-serif',
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: '0.02em',
              }}
            >
              {nav.map((item, i) => (
                <PortalLink
                  key={i}
                  href={item.href ?? '#'}
                  style={{ color: '#fff', textDecoration: 'none' }}
                >
                  {tx(item.label)}
                </PortalLink>
              ))}
              {joinText && (
                <PortalLink
                  href={joinHref}
                  style={{
                    color: '#fff',
                    textDecoration: 'none',
                    border: '1px solid #fff',
                    padding: '4px 8px',
                  }}
                >
                  {tx(joinText)}
                </PortalLink>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: 'clamp(28px, 8vh, 96px)', maxWidth: 460 }}>
          {eyebrow && (
            <div
              style={{
                display: 'inline-block',
                background: '#fff',
                color: '#111',
                borderRadius: 2,
                padding: '4px 7px',
                fontFamily: 'Arial, sans-serif',
                fontSize: 11,
                fontWeight: 900,
              }}
            >
              {tx(eyebrow)}
            </div>
          )}
          <div
            style={{
              marginTop: 14,
              fontSize: 'clamp(38px, 7vw, 82px)',
              lineHeight: 0.88,
              fontWeight: 900,
              letterSpacing: '-0.04em',
            }}
          >
            {titleLines.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(16px, 3vw, 32px)' }}>
          <PortalLink
            href={playHref}
            aria-label={t('hero.wildTv.play')}
            style={{
              width: 68,
              height: 68,
              flexShrink: 0,
              border: '2px solid rgba(255,255,255,0.9)',
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
            }}
          >
            <Play aria-hidden size={26} fill="currentColor" style={{ marginLeft: 3 }} />
          </PortalLink>
          {nextHref && (
            <PortalLink
              href={nextHref}
              aria-label={t('hero.wildTv.nextEpisode')}
              style={{
                width: 44,
                height: 44,
                flexShrink: 0,
                borderRadius: '50%',
                background: accentColor,
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
              }}
            >
              <ChevronRight aria-hidden size={22} />
            </PortalLink>
          )}
        </div>

        {(rail || eps.length > 0) && (
          <div style={{ marginTop: 'clamp(20px, 3vh, 28px)' }}>
            {rail && (
              <PortalLink
                href={ctaHref}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  color: '#fff',
                  textDecoration: 'none',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: 15,
                  fontWeight: 900,
                  marginBottom: 12,
                }}
              >
                {rail}
                <ChevronRight aria-hidden size={16} />
              </PortalLink>
            )}
            {eps.length > 0 && (
              <div
                className="wild-tv-hero-rail"
                style={{
                  display: 'flex',
                  gap: 14,
                  overflowX: 'auto',
                  scrollSnapType: 'x mandatory',
                  paddingBottom: 4,
                }}
              >
                {eps.map((ep, i) => (
                  <PortalLink
                    key={i}
                    href={ep.href ?? '#'}
                    style={{
                      position: 'relative',
                      flex: '0 0 clamp(140px, 20vw, 200px)',
                      height: 116,
                      borderRadius: 4,
                      overflow: 'hidden',
                      scrollSnapAlign: 'start',
                      background: ep.imageUrl
                        ? `url("${ep.imageUrl}") center/cover`
                        : 'linear-gradient(135deg, #333, #111)',
                    }}
                  >
                    {ep.label && (
                      <span
                        style={{
                          position: 'absolute',
                          left: 8,
                          bottom: 8,
                          padding: '3px 5px',
                          borderRadius: 2,
                          background: '#fff',
                          color: '#111',
                          fontFamily: 'Arial, sans-serif',
                          fontSize: 12,
                          fontWeight: 900,
                        }}
                      >
                        {tx(ep.label)}
                      </span>
                    )}
                    {ep.duration && (
                      <span
                        style={{
                          position: 'absolute',
                          right: 8,
                          bottom: 8,
                          padding: '3px 5px',
                          borderRadius: 2,
                          background: 'rgba(0,0,0,0.72)',
                          border: '1px solid rgba(255,255,255,0.6)',
                          color: '#fff',
                          fontFamily: 'Arial, sans-serif',
                          fontSize: 12,
                          fontWeight: 800,
                        }}
                      >
                        {ep.duration}
                      </span>
                    )}
                  </PortalLink>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 640px) {
          .wild-tv-hero-nav a:not(:last-child) { display: none; }
        }
        .wild-tv-hero-rail::-webkit-scrollbar { height: 0; }
      `,
        }}
      />
    </BlockRoot>
  );
}
